import React, { useState, useRef, useEffect } from 'react';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface ImageCropperProps {
  imageSrc: string;
  onCancel: () => void;
  onCropComplete: (croppedImage: string) => void;
}

const ImageCropper: React.FC<ImageCropperProps> = ({
  imageSrc,
  onCancel,
  onCropComplete
}) => {
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 90,
    height: 90,
    x: 5,
    y: 5
  });
  const [completeCrop, setCompleteCrop] = useState<PixelCrop | null>(null);
  const [imageType, setImageType] = useState<'vertical' | 'horizontal' | 'square'>('square');
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const imgRef = useRef<HTMLImageElement>(null);
  const cropContainerRef = useRef<HTMLDivElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  // Precargar la imagen para determinar su orientación y dimensiones
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const { width, height } = img;
      
      setImageDimensions({ width, height });
      
      // Determinar tipo de imagen con un margen de tolerancia para imágenes casi cuadradas
      if (Math.abs(width - height) < 10) {
        setImageType('square');
      } else if (height > width) {
        setImageType('vertical');
      } else {
        setImageType('horizontal');
      }
    };
    img.src = imageSrc;
  }, [imageSrc]);

  // Función para obtener un recorte centrado con proporción 1:1
  function centerAspectCrop(
    mediaWidth: number,
    mediaHeight: number,
    aspect: number
  ) {
    return centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        aspect,
        mediaWidth,
        mediaHeight
      ),
      mediaWidth,
      mediaHeight
    );
  }

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    
    // Guardar las dimensiones reales de la imagen mostrada
    setImageDimensions({ width, height });
    
    // Calcular y establecer un recorte centrado con proporción 1:1
    const newCrop = centerAspectCrop(width, height, 1);
    setCrop(newCrop);
    
    // Inicializar también completeCrop con un valor por defecto
    // para que el usuario pueda hacer clic en Aplicar inmediatamente
    const pixelCrop: PixelCrop = {
      unit: 'px',
      x: Math.round((width * newCrop.x) / 100),
      y: Math.round((height * newCrop.y) / 100),
      width: Math.round((width * newCrop.width) / 100),
      height: Math.round((height * newCrop.height) / 100)
    };
    setCompleteCrop(pixelCrop);
  };

  const createCroppedImage = () => {
    if (!imgRef.current) {
      console.error("No se ha cargado la imagen correctamente");
      return;
    }
    
    // Si completeCrop es null, usar el crop actual para generar uno
    if (!completeCrop) {
      if (!crop.width || !crop.height) {
        console.error("No se ha seleccionado ningún área de recorte");
        return;
      }
      
      const image = imgRef.current;
      const pixelCrop: PixelCrop = {
        unit: 'px',
        x: Math.round((image.width * (crop.x || 0)) / 100),
        y: Math.round((image.height * (crop.y || 0)) / 100),
        width: Math.round((image.width * crop.width) / 100),
        height: Math.round((image.height * crop.height) / 100)
      };
      setCompleteCrop(pixelCrop);
      
      // Usar el pixelCrop generado
      processCrop(pixelCrop);
    } else {
      // Usar el completeCrop existente
      processCrop(completeCrop);
    }
  };
  
  const processCrop = (cropToUse: PixelCrop) => {
    if (!imgRef.current) return;
    
    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error("No se pudo crear el contexto del canvas");
      return;
    }

    // Obtener las dimensiones reales de la imagen
    const { naturalWidth, naturalHeight, width, height } = image;

    // Calcular la escala entre la imagen mostrada y su tamaño natural
    const scaleX = naturalWidth / width;
    const scaleY = naturalHeight / height;

    // Establecer el tamaño de salida
    const outputSize = 300;
    canvas.width = outputSize;
    canvas.height = outputSize;

    // Habilitar suavizado para mejor calidad
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Calcular coordenadas y dimensiones precisas del recorte
    const cropX = cropToUse.x * scaleX;
    const cropY = cropToUse.y * scaleY;
    const cropWidth = cropToUse.width * scaleX;
    const cropHeight = cropToUse.height * scaleX; // Asegurar que sea cuadrado

    // Dibujar solo la parte recortada de la imagen original en el lienzo
    try {
      ctx.drawImage(
        image,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        outputSize,
        outputSize
      );

      // Convertir el lienzo a una imagen y llamar al callback
      const croppedImage = canvas.toDataURL('image/jpeg', 0.95);
      onCropComplete(croppedImage);
    } catch (error) {
      console.error("Error al procesar la imagen recortada:", error);
    }
  };

  // Calcular clases para la imagen según el tipo
  const getImageClasses = () => {
    switch (imageType) {
      case 'vertical':
        return "max-h-full max-w-full w-auto object-contain";
      case 'square':
        return "max-h-full max-w-full w-auto object-contain";
      case 'horizontal':
        return "max-h-full max-w-[90%] w-auto object-contain";
      default:
        return "max-h-full max-w-[90%] w-auto object-contain";
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 p-4 pb-16 rounded-xl w-full max-w-xl flex flex-col max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold text-white mb-3">
          Ajusta tu foto de perfil
        </h3>
        
        <div 
          ref={cropContainerRef} 
          className="bg-gray-900 rounded-lg overflow-hidden flex items-center justify-center relative 
                     max-h-[60vh]"
        >
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => {
              console.log("Recorte completado:", c);
              setCompleteCrop(c);
            }}
            aspect={1}
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          >
            <img 
              ref={imgRef}
              src={imageSrc}
              alt="Recortar foto"
              onLoad={onImageLoad}
              className={getImageClasses()}
              crossOrigin="anonymous"
              style={{ 
                maxHeight: '100%',
                maxWidth: '100%'
              }}
            />
          </ReactCrop>
        </div>
        
        {/* Canvas oculto para referencia (ayuda en depuración) */}
        <canvas
          ref={previewCanvasRef}
          style={{
            display: 'none',
            width: 0,
            height: 0,
          }}
        />

        <div className="flex justify-between items-center mt-4 sticky bottom-0 bg-gray-800 pt-3 pb-1">
          <p className="text-sm text-gray-300">
            Mueve o ajusta el recuadro
          </p>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm text-gray-300 hover:text-white"
            >
              Cancelar
            </button>
            <button
              onClick={createCroppedImage}
              className="px-4 py-2 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-lg"
            >
              Aplicar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper; 